import { CheckCircle } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/emergencySla";

/* Pulsing dot: amber at 50%, red at 75% and beyond. */
export function SlaLadderBeacon({ sla, className = "", size = "h-2.5 w-2.5" }) {
  const level = sla?.ui?.beacon;
  if (!level) return null;
  const color = level === "red" ? "bg-red-500" : "bg-amber-500";
  return (
    <span className={`relative inline-flex shrink-0 ${size} ${className}`} title={sla.ui.label} data-testid={`sla-ladder-beacon-${sla.tier}`}>
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} ${sla.tier === "breached" ? "opacity-90" : "opacity-70"}`} />
      <span className={`relative inline-flex rounded-full ${size} ${color}`} />
    </span>
  );
}

/* Tier label pill (amber / red / breached / met / late). */
export function SlaTierBadge({ sla, testId }) {
  if (!sla?.ui?.label) return null;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${sla.ui.badge}`} data-testid={testId} data-tier={sla.tier}>
      {sla.ui.label}
    </span>
  );
}

/* Elapsed / remaining block with the progress bar; freezes at arrival ("Responded in …"). */
export function SlaTimer({ sla, compact = false, status, testId }) {
  const big = compact ? "text-sm" : "text-base";
  const small = compact ? "text-[8px]" : "text-[9px]";
  if (sla.tier === "done") {
    return (
      <div className="flex items-center gap-1 text-muted-foreground justify-end" data-testid={testId}>
        <CheckCircle size={14} className="text-emerald-500" />
        <span className="text-[10px]">{status === "completed" ? "Done" : "Cancelled"}</span>
      </div>
    );
  }
  if (sla.arrived) {
    return (
      <div className="text-right" data-testid={testId} data-tier={sla.tier}>
        <p className={`${big} font-bold font-mono tabular-nums ${sla.ui.text}`}>{fmtDuration(sla.elapsed)}</p>
        <p className={`${small} uppercase tracking-wider ${sla.ui.text}`}>{sla.tier === "met" ? "Responded · SLA met" : `Responded · ${fmtDuration(-sla.remaining)} late`}</p>
        <div className="w-full h-1.5 bg-muted rounded-full mt-1.5 overflow-hidden">
          <div className={`h-full rounded-full ${sla.ui.bar}`} style={{ width: `${Math.min(100, sla.pct)}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="text-right" data-testid={testId} data-tier={sla.tier}>
      <p className={`${big} font-bold font-mono tabular-nums ${sla.ui.text} ${sla.tier === "breached" ? "animate-pulse" : ""}`}>{fmtDuration(sla.elapsed)}</p>
      <p className={`${small} text-muted-foreground uppercase tracking-wider`}>Elapsed</p>
      <div className="w-full h-1.5 bg-muted rounded-full mt-1.5 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${sla.ui.bar}`} style={{ width: `${Math.min(100, sla.pct)}%` }} />
      </div>
      <p className={`${small} mt-0.5 ${sla.tier === "breached" ? sla.ui.text : "text-muted-foreground"}`}>
        {sla.tier === "breached" ? `Over by ${fmtDuration(-sla.remaining)}` : `${fmtDuration(sla.remaining)} left · ${Math.round(sla.pct)}%`}
      </p>
    </div>
  );
}
